import { useCallback, useMemo, useState } from "react";
import { Chip, Grid, Button } from "@mui/material";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Reach from "../Reach";
import { styled } from "@mui/system";
import { parseISO } from "date-fns";
import { CampaignFromInsight, Campaigns } from "../../../../../types/campaigns";
import { getDate } from "../../../../../utils/date";
import { price, urlify } from "../../../../../utils/format";
import {
  severity,
  STATUS_CAMPAIGN2,
  STATUS_CAMPAIGN_ADS,
  STATUS_CAMPAIGN_ADS2,
} from "../../../../../utils/constants";
import {
  AlertCustom,
  ButtonCancelCampaign,
  TypographyButtonCancel,
  TypographyLearnMore,
  B,
} from "../styles";
import { Timeline } from "../../../../../components/Timeline";
import Detailed from "../Detailed";

const Div = styled("div")({});
const Img = styled("img")({});

interface ResumeProps {
  overview?: any;
  reach?: {
    quantidade: unknown;
    status_envio_externo: string;
  }[];
  campaign: Campaigns;
  insights?: CampaignFromInsight;
  onClose: () => void;
  onPause: (id_campanha: number) => void;
  onPauseAds: (id: number) => void;
  onContinueAds: (id: number) => void;
  onContinue: (id_campanha: number) => void;
  onResend: (id: number, type: string) => void;
}

export default function Resume({
  overview,
  reach,
  campaign,
  insights,
  onClose,
  onPause,
  onContinue,
  onContinueAds,
  onPauseAds,
  onResend,
}: ResumeProps) {
  const [learnMore, setLearnMore] = useState<Boolean>(true);
  const DATE_NOW = parseISO(new Date().toISOString());

  const handleLearnMore = useCallback(() => {
    setLearnMore((prev) => !prev);
  }, []);

  const isLearnMore = useMemo(() => {
    return insights && insights?.mensagem?.length > 67;
  }, [insights]);

  const canPauseAds = useMemo(() => {
    return Boolean(
      campaign.tipo_campanha === "ADS" &&
        campaign.status ===
          ("ACTIVE" ||
            "PENDING_REVIEW" ||
            "PREAPPROVED" ||
            "IN_PROCESS" ||
            "PENDING_BILLING_INFO") &&
        new Date() < parseISO(campaign.data_fim_campanha)
    );
  }, [campaign.tipo_campanha, campaign.status, campaign.data_fim_campanha]);

  const canContinueAds = useMemo(() => {
    return Boolean(
      campaign.tipo_campanha === "ADS" &&
        campaign.status === ("PAUSED" || "CAMPAIGN_PAUSED" || "ADSET_PAUSED") &&
        new Date() < parseISO(campaign.data_fim_campanha)
    );
  }, [campaign.tipo_campanha, campaign.status, campaign.data_fim_campanha]);

  const canContinueCampaign = useMemo(() => {
    return Boolean(
      campaign.tipo_campanha !== "ADS" &&
        campaign.processado === 3 &&
        parseISO(campaign.data_fim_campanha) > new Date()
    );
  }, [campaign.tipo_campanha, campaign.processado, campaign.data_fim_campanha]);

  const canPauseCampaign = useMemo(() => {
    return Boolean(
      campaign.tipo_campanha !== "ADS" &&
        (campaign.processado === 0 || campaign.processado === 1) &&
        new Date() < parseISO(campaign.data_fim_campanha)
    );
  }, [campaign.tipo_campanha, campaign.processado, campaign.data_fim_campanha]);

  const campaignStatusText = useMemo(() => {
    let effective_status = campaign.effective_status;

    if (campaign.tipo_campanha === "ADS") {
      if (
        DATE_NOW < parseISO(campaign.data_inicio_campanha) &&
        effective_status.includes("ACTIVE")
      ) {
        effective_status = "SCHEDULED";
      }
      if (
        DATE_NOW > parseISO(campaign.data_fim_campanha) &&
        effective_status.includes("ACTIVE")
      ) {
        effective_status = "FINISH";
      }
      if (
        DATE_NOW > parseISO(campaign.data_fim_campanha) &&
        (!effective_status.includes("ACTIVE") ||
          !effective_status.includes("FINISH"))
      ) {
        effective_status = "FINISH_2";
      }
      return STATUS_CAMPAIGN_ADS[effective_status];
    }

    if (
      campaign.tipo_campanha !== "ADS" &&
      campaign.status_envio.includes("ERRO")
    ) {
      return STATUS_CAMPAIGN2[4];
    }
    return STATUS_CAMPAIGN2[campaign.processado];
  }, [
    DATE_NOW,
    campaign.status_envio,
    campaign.data_fim_campanha,
    campaign.data_inicio_campanha,
    campaign.effective_status,
    campaign.processado,
    campaign.tipo_campanha,
  ]);

  const severityAlertCustom = useMemo(() => {
    if (
      campaign.tipo_campanha !== "ADS" &&
      campaign.status_envio.includes("ERRO")
    ) {
      return severity[4];
    }
    if (campaign.tipo_campanha !== "ADS") {
      return severity[campaign.processado];
    }
    if (campaign.tipo_campanha === "ADS") {
      return severity[STATUS_CAMPAIGN_ADS2[campaign.effective_status]];
    }
  }, [
    campaign.tipo_campanha,
    campaign.status_envio,
    campaign.processado,
    campaign.effective_status,
  ]);

  const colorAlertCustom = useMemo(() => {
    const color: Array<string> = [
      "#E6F7FF",
      "#E6F7FF",
      "#F6FFED",
      "#FDFAE6",
      "#ffbdb9",
    ];
    if (
      campaign.tipo_campanha !== "ADS" &&
      campaign.status_envio.includes("ERRO")
    ) {
      return color[4];
    }
    if (campaign.tipo_campanha !== "ADS") {
      return color[campaign.processado];
    }
    if (campaign.tipo_campanha === "ADS") {
      return color[STATUS_CAMPAIGN_ADS2[campaign.effective_status]];
    }
  }, [
    campaign.tipo_campanha,
    campaign.status_envio,
    campaign.processado,
    campaign.effective_status,
  ]);

  const icon = useMemo(() => {
    switch (campaign.tipo_campanha) {
      case "INSTAGRAM":
      case "INSTAGRAM_CAROUSEL":
        return "https://datasalesio-imagens.s3.amazonaws.com/tmp-jhgs/icons/Instagram+1.jpg";
      case "FACEBOOK":
        return "https://datasalesio-imagens.s3.amazonaws.com/tmp-jhgs/icons/Facebook+-+Original.png";
      case "WHATSAPP":
      case "WHATSAPP_GROUP":
        return "https://datasalesio-imagens.s3.amazonaws.com/tmp-jhgs/icons/whatsapp+1.png";
      case "SMS":
        return "https://datasalesio-imagens.s3.amazonaws.com/tmp-jhgs/icons/sms.png";
      case "SMS_V2":
        return "https://datasalesio-imagens.s3.amazonaws.com/tmp-jhgs/icons/sms.png";
      case "ADS":
        return "https://datasalesio-imagens.s3.amazonaws.com/tmp-jhgs/icons/turbinada.png";
    }
  }, [campaign.tipo_campanha]);

  const tabs = [
    {
      label: "Resumo",
      index: 0,
      children: (
        <Grid item container>
          <Grid item>
            <AlertCustom
              severity={severityAlertCustom}
              sx={{
                background: colorAlertCustom,
              }}
            >
              Status: {campaignStatusText}
            </AlertCustom>
          </Grid>
          {campaign.tipo_campanha === "ADS" &&
            (canPauseCampaign ||
              canContinueCampaign ||
              canPauseAds ||
              canContinueAds) && (
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  marginTop: "8px",
                }}
              >
                <AlertCustom
                  severity={"warning"}
                  sx={{
                    background: "#FDFAE6",
                    fontSize: "12px",
                  }}
                >
                  Atenção após o início da campanha, os seus creditos não
                  poderão ser restituídos.
                </AlertCustom>
              </Grid>
            )}
          <Grid
            container
            spacing={2}
            sx={{ marginTop: "18px", fontSize: "13px" }}
          >
            {Boolean(campaign.nome_campanha) && (
              <Grid item>
                <B>Nome: </B>
                {campaign.nome_campanha}
              </Grid>
            )}
            {campaign.tipo_campanha !== "ADS" && insights?.data_insert && (
              <Grid item>
                <>
                  <B>Data de criação: </B>
                  {getDate(
                    "dd/MM/yyyy HH:mm",
                    insights.data_insert.replace("Z", "")
                  )}
                </>
              </Grid>
            )}
            {campaign.tipo_campanha === "ADS" && (
              <Grid item>
                <>
                  <B>Inicio da campanha: </B>
                  {getDate(
                    "dd/MM/yyyy HH:mm",
                    parseISO(campaign.data_inicio_campanha)
                  )}
                </>
              </Grid>
            )}
            <Grid item>
              {campaign.tipo_campanha !== "ADS" && campaign.data_envio && (
                <>
                  <B>Data de agendamento: </B>
                  {getDate(
                    "dd/MM/yyyy HH:mm",
                    campaign.data_envio.replace("Z", "")
                  )}
                </>
              )}
            </Grid>
            {campaign.tipo_campanha !== "ADS" && insights?.data_processado && (
              <Grid item>
                <>
                  <B>Término do processo: </B>{" "}
                  {getDate(
                    "dd/MM/yyyy HH:mm",
                    insights?.data_processado.replace("Z", "")
                  )}
                </>
              </Grid>
            )}
            {campaign.tipo_campanha === "ADS" && campaign.data_fim_campanha && (
              <>
                <Grid item>
                  <B>Fim da campanha: </B>
                  {getDate(
                    "dd/MM/yyyy HH:mm",
                    parseISO(campaign.data_fim_campanha)
                  )}
                </Grid>
              </>
            )}
            {campaign.tipo_campanha === "ADS" && campaign.created_time && (
              <>
                <Grid item>
                  <B>Data de criação: </B>
                  {getDate("dd/MM/yyyy HH:mm", parseISO(campaign.created_time))}
                </Grid>
              </>
            )}
            {campaign.tipo_campanha !== "ADS" && insights?.custo_campanha && (
              <Grid item>
                <B>Custos: </B>
                {price(insights?.custo_campanha)}
              </Grid>
            )}
            {campaign.tipo_campanha !== "ADS" && insights?.redirect_tracker && (
              <Grid item>
                <B>Redirecionamento: </B>
                <div
                  dangerouslySetInnerHTML={{
                    __html: urlify(insights?.redirect_tracker),
                  }}
                />
              </Grid>
            )}
            {insights?.mensagem &&
              !campaign.tipo_campanha.includes("WHATSAPP") &&
              !campaign.tipo_campanha.includes("SMS") && (
                <Grid item display={"flex"} alignItems={"baseline"}>
                  <B style={{ marginRight: "4px" }}>Mensagem:</B>
                  <div>
                    <TypographyLearnMore
                      sx={{
                        ...(learnMore && {
                          whiteSpace: "nowrap",
                        }),
                      }}
                    >
                      {insights.mensagem}
                    </TypographyLearnMore>
                    {isLearnMore && (
                      <Button
                        onClick={() => handleLearnMore()}
                        sx={{ marginTop: "4px", marginLeft: "-7px" }}
                      >
                        {learnMore ? "Ler mais" : "ocultar"}
                      </Button>
                    )}
                  </div>
                </Grid>
              )}
            <Grid item container spacing={2} xs={12}>
              <Grid item>
                <Chip
                  label={
                    <B>#{campaign.id_campanha ? campaign.id_campanha : null}</B>
                  }
                  variant="outlined"
                  color="primary"
                  size="small"
                />
              </Grid>
              <Grid item>
                <Chip
                  label={campaign.tipo_campanha}
                  variant="outlined"
                  color="primary"
                  size="small"
                />
              </Grid>
            </Grid>
            <Grid
              item
              container
              sx={{ display: "block", justifyContent: "space-between" }}
            >
              <Reach
                // @ts-ignore: Unreachable code error
                data={campaign.tipo_campanha !== "ADS" ? reach : overview}
              />
            </Grid>
          </Grid>
        </Grid>
      ),
    },
    {
      disabled: !campaign.tipo_campanha.includes("WHATSAPP"),
      label: "Detalhado",
      index: 1,
      children: <Detailed id={campaign.id_campanha} />,
    },
  ];

  return (
    <>
      <Grid
        sx={{
          display: "flex",
          alignItems: "center",
          height: "2.5rem",
          justifyContent: "space-between",
          marginBottom: "2vh",
        }}
      >
        <Div sx={{ display: "flex", alignItems: "center" }}>
          <Img
            src={icon}
            alt="icon"
            style={{ width: "1.4rem", height: "1.4rem" }}
          />
          <Typography
            style={{
              marginLeft: "8px",
              fontSize: "20px",
              fontWeight: 500,
              lineHeight: "26px",
              letterSpacing: "0.05rem",
            }}
          >
            {campaign.tipo_campanha !== "ADS"
              ? campaign.nome_campanha
                ? campaign.nome_campanha
                : campaign.id_campanha
              : `Ação Turbinada`}
          </Typography>
        </Div>

        <Stack direction="row" spacing={1} alignItems="center">
          {campaign.tipo_campanha.includes("WHATSAPP") && (
            <ButtonCancelCampaign
              onClick={() => {
                onResend(campaign.id_campanha, campaign.tipo_campanha);
              }}
            >
              <TypographyButtonCancel>Reenviar campanha</TypographyButtonCancel>
            </ButtonCancelCampaign>
          )}

          {(canPauseCampaign ||
            canContinueCampaign ||
            canPauseAds ||
            canContinueAds) && (
            <ButtonCancelCampaign
              onClick={() => {
                if (canPauseCampaign) {
                  onPause(campaign.id_campanha);
                  onClose();
                }
                if (canContinueCampaign) {
                  onContinue(campaign.id_campanha);
                  onClose();
                }
                if (canPauseAds) {
                  onPauseAds(campaign.id);
                  onClose();
                }
                if (canContinueAds) {
                  onContinueAds(campaign.id);
                  onClose();
                }
              }}
            >
              {(canPauseCampaign || canPauseAds) && (
                <TypographyButtonCancel>Pausar campanha</TypographyButtonCancel>
              )}
              {(canContinueCampaign || canContinueAds) && (
                <TypographyButtonCancel>
                  Retomar campanha
                </TypographyButtonCancel>
              )}
            </ButtonCancelCampaign>
          )}
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Timeline tabs={tabs} padding="0px 0 0 0" />
      </Grid>
    </>
  );
}
